
// API.ts

import axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { DGAppConfig } from "./dg/DGConfigModal";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE || "";

const _NO_CACHE_HEADERS = {
  "Cache-Control": "no-cache, no-store, must-revalidate",
  "Pragma":        "no-cache",
  "Expires":       "0",
};

Object.assign(axios.defaults.headers.common, _NO_CACHE_HEADERS);

export const api = axios.create({
  baseURL: axios.defaults.baseURL,
  withCredentials: true,
  timeout: 60000,
  headers: { ..._NO_CACHE_HEADERS },
});

function handleUnauthorized() {
  // Called on any 401. The app registers the real handler; default is a hard redirect.
  const h = (window as any).__adriva_onSessionExpired;
  if (typeof h === "function") h();
  else window.location.href = "/";   // fallback if handler not registered yet
}

function attach401Handler(client: AxiosInstance) {
  client.interceptors.response.use(
    (resp) => resp,
    (error) => {
      if (error?.response?.status === 401) {
        const url = error.config?.url || "";
        const isAuthCall = url.includes("/auth/login") || url.includes("/auth/me");
        if (!isAuthCall) handleUnauthorized();
      }
      return Promise.reject(error);
    },
  );
}

attach401Handler(api);
attach401Handler(axios);

// Auto-append timestamp query param on every GET to defeat URL caching
api.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() === "get") {
    config.params = { ...(config.params || {}), _t: Date.now() };
  }
  return config;
});

axios.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() === "get") {
    config.params = { ...(config.params || {}), _t: Date.now() };
  }
  return config;
});

console.log('process.env.REACT_APP_API_BASE - ' + process.env.REACT_APP_API_BASE);

//////////////////////////////
// Generic helpers
//////////////////////////////

export async function apiGet<T = any>(
  path: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api.get(path, { params, ...config });
    return response.data;
  } catch (err: any) {
    throw err?.response?.data || err;
  }
}

export async function apiPost<T = any>(
  path: string,
  body?: Record<string, any>,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api.post(path, body, config);
    return response.data;
  } catch (err: any) {
    throw err?.response?.data || err;
  }
}

export async function apiPut<T = any>(
  path: string,
  body?: Record<string, any>,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api.put(path, body, config);
    return response.data;
  } catch (err: any) {
    throw err?.response?.data || err;
  }
}

export async function apiDelete<T = any>(
  path: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api.delete(path, { params, ...config });
    return response.data;
  } catch (err: any) {
    throw err?.response?.data || err;
  }
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function auth_login(
  email: string,
  password: string
): Promise<{ success: boolean; user_id: string; email: string; full_name: string; is_admin: boolean,
  entitlements?: { modules: string[]; tools: string[]; apps: "*" | string[] };
 }> {
  const res = await api.post(
    "/adriva_api/auth/login",
    { email, password },
    { withCredentials: true }
  );
  return res.data;
}

export async function auth_logout(): Promise<void> {
  await api.post("/adriva_api/auth/logout", {}, { withCredentials: true });
}

export async function auth_me(): Promise<{
  user_id: string; email: string; full_name: string;
  is_admin: boolean; last_login: string | null;
}> {
  const res = await api.get("/adriva_api/auth/me", { withCredentials: true });
  return res.data;
}

export async function auth_requestReset(email: string): Promise<void> {
  await api.post("/adriva_api/auth/request-reset", { email });
}

export async function auth_resetPassword(
  token: string,
  new_password: string,
  confirm: string
): Promise<void> {
  await api.post("/adriva_api/auth/reset-password", { token, new_password, confirm });
}

export async function auth_changePassword(
  current_password: string,
  new_password: string,
  confirm: string
): Promise<void> {
  await api.post(
    "/adriva_api/auth/change-password",
    { current_password, new_password, confirm },
    { withCredentials: true }
  );
}


interface Payload {
  [key: string]: any;
  csvFiles?: File[] | FileList;
  businessFiles?: File[] | FileList; 
  dg_config?: DGAppConfig | null;
  sheet_config?: string | null;
}

export interface UploadFromConnectionPayload {
  // ── Pipeline metadata (mirrors the uploadFiles form fields) ──────────────
  tool: string;
  action?: string;
  app_nm: string;
  business_context?: string;
  domain?: string;
  entity?: string;
  system_name?: string;

  // ── DB connection selection (from SchemaExplorer / ConnectionManagerModal)
  connection_id: string;
  schema: string;
  /** Maps bare table name → its schema. Required when selected tables span
   *  multiple schemas (e.g. { orders: "public", invoices: "billing" }).
   *  Omit or pass {} for single-schema selections — the server falls back
   *  to `schema` for every table in that case. */
  table_schemas?: Record<string, string>;
  tables: string[];
  columns?: Record<string, string[]>;        // per-table column list; empty = SELECT *
  relationships?: Array<{
    from_schema: string;
    from_table: string;
    from_column: string;
    to_schema: string;
    to_table: string;
    to_column: string;
  }>;
  custom_sql?: string;                       // overrides tables/columns when set

  // ── Extraction options ────────────────────────────────────────────────────
  row_limit?: number;                        // default 500 000 on the server

  dg_config?: DGAppConfig | null;
  sheet_config?: string | null;
}

export interface UploadFromConnectionResponse {
  success: boolean;
  process_id?: string;
  entity_id?: string;
  status?: string;
  message?: string;
}

// export function adriva_getAppList(tool: string, success:any, error:any){
//     axios.post('/adriva_api/getAppList',{tool}).then((response:any)=> success(response.data)).catch(error);
// }

export function adriva_getAppList(tool: string, success:any, error:any){
    api.post('/adriva_api/getAppList',{tool}).then((response:any)=> success(response.data)).catch(error);
}

export function adriva_getConfig(input: string, success:any, error:any){
    axios.post('/adriva_api/getConfig',{input}).then((response:any)=> success(response.data)).catch(error);
}

export function adriva_postAction(input: string, success:any, error:any){
    axios.post('/adriva_api/action',{input}).then((response:any)=> success(response.data)).catch(error);
}



export async function adriva_getProcessStatus(processId: string): Promise<any> {
  const response = await axios.get(`/adriva_api/process-status/${processId}`);
  return response.data;
}

export function adriva_getAppProcessStatus(appName: string, success:any, error:any){
    axios.get(`/adriva_api/app-process-status/${appName}`).then((response:any)=> success(response.data)).catch(error);
}

export function adriva_getJobs(appName: string, success:any, error:any){
    axios.get(`/adriva_api/job/jobs/${appName}`).then((response:any)=> success(response.data)).catch(error);
}

export const adriva_deleteJob = (jobId: string) =>
  apiDelete(`/adriva_api/job/job/${jobId}`);

export const adriva_getJobsByEntity = (
  entityId:  string,
  onSuccess: (res: any) => void,
  onError:   (err: any) => void
): void => {
  axios.get(`/adriva_api/job/jobs/entity/${entityId}`).then((response:any)=> onSuccess(response.data)).catch(onError);
};

export function adriva_getJobDetails(jobId: string, success:any, error:any){
    axios.get(`/adriva_api/job/job/${jobId}`).then((response:any)=> success(response.data)).catch(error);
}

export async function adriva_sendChatMsg(appName: string, message: string, sessionId: string, addlInput: string): Promise<any> {
  const response = await axios.post('/adriva_api/agentic/sendChatMsg', {appName, message, sessionId,  addlInput});
  return response.data;
}

// ═══════════════════════════════════════════════════════════════════════════════
// PASTE INTO: src/pages/API.ts  (at the bottom, alongside other exports)
// ═══════════════════════════════════════════════════════════════════════════════

// ── Sheet validation types ────────────────────────────────────────────────────

export interface ServerSheetIssue {
  rule_id:        string;
  severity:       "error" | "warning" | "info";
  message:        string;
  detail?:        string;
  rows?:          number[];
  column?:        string;
  count?:         number;
  sample_values?: string[];
}

export interface ServerSheetValidation {
  sheet_name:        string;
  status:            "pass" | "warn" | "error";
  row_count:         number;
  col_count:         number;
  header_row:        number;
  detected_columns:  Record<string, string | null>;
  summary:           string;
  issues:            ServerSheetIssue[];
}

export interface ValidateSheetsResponse {
  success: boolean;
  status:  "pass" | "warn" | "error";
  tool:    string;
  sheets:  ServerSheetValidation[];
}

/**
 * Server-side deep validation of uploaded sheets.
 * Called by FileSource.tsx after client-side classification, before submission.
 * Uses YAML-driven rules specific to the selected tool (DG/DQ/MDM).
 *
 * Does NOT block uploadFiles — this is advisory validation only.
 */
export async function adriva_validateSheets(
  tool:  string,
  files: File[],
): Promise<ValidateSheetsResponse> {
  const formData = new FormData();
  formData.append("tool", tool);
  files.forEach(f => formData.append("csvFiles", f));

  const res = await api.post("/adriva_api/validate-sheets", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,  // 30s — validation should be fast
  });
  return res.data;
}


/////////////////////////DDM .............................

export async function adriva_uploadFiles(payload: Payload): Promise<any> {
    const formData = new FormData();
    const fileKeys = ['csvFiles','businessFiles'];
    // Append all keys except csvFiles
    Object.entries(payload).forEach(([key, value]) => {
        if (fileKeys.indexOf(key) >-1) return;

        if (value !== undefined && value !== null) {
            formData.append(key, value);
        }
    });

    // Append CSV files
    if (payload.csvFiles) {
        const filesArray = Array.isArray(payload.csvFiles)
        ? payload.csvFiles
        : Array.from(payload.csvFiles);

        filesArray.forEach((file) => formData.append("csvFiles", file));
    }

     // Append businessFiles files
    if (payload.businessFiles) {
        const filesArray = Array.isArray(payload.businessFiles)
        ? payload.businessFiles
        : Array.from(payload.businessFiles);

        filesArray.forEach((file) => formData.append("businessFiles", file));
    }

    // When sending — serialize as JSON string in FormData for uploadFiles:
    if (payload.dg_config) {
      formData.append("dg_config", JSON.stringify(payload.dg_config));
    }

    if (payload.sheet_config) {
      formData.append("sheet_config", payload.sheet_config);
    }

    const res = await axios.post('/adriva_api/uploadFiles', formData, {
        headers: { "Content-Type": "multipart/form-data" }
    });

    return res.data;
}

export async function adriva_ddm_uploadFiless(appNm: string, businessConext: string, domain: string, entity: string,
    csvFiles: FileList): Promise<any> {
  const formData = new FormData();
  formData.append("app_nm", appNm);
  formData.append("business_context", businessConext);
  formData.append("domain", domain);
  formData.append("entity", entity);
  Array.from(csvFiles).forEach((file) => {
    formData.append("csvFiles", file);
  });

  


  const res = await axios.post('/adriva_api/uploadFiles', formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });

  return res.data;
}


export async function adriva_ddm_mdm_source_uploadFiles(
    appNm: string,
    domain: string, 
    entity: string,
    src_id: string,
    csvFiles: FileList
    ): Promise<any> {

    const formData = new FormData();
    formData.append("app_nm", appNm);
    formData.append("domain", domain);
    formData.append("entity", entity);
    formData.append("src_id", src_id);
    Array.from(csvFiles).forEach((file) => {
        formData.append("csvFiles", file);
    });


  const res = await axios.post('/ddm-mdm-source-uploadFiles', formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });

  return res.data;
}


export async function adriva_executeDQWithUpload(
    metadata: Payload,
    fileMap: Record<string, File | Blob>
): Promise<{ success: boolean; job_id?: string; message?: string }> {
    const formData = new FormData();

    // Append files to FormData, using the prefix as the form field name
    Object.keys(fileMap).forEach((prefix) => {
        const file = fileMap[prefix];
        if (file) {
            // Append the file using the prefix (e.g., 'MARA') as the key
            formData.append(prefix, file, (file as File).name);
        }
    });

    // Append the JSON metadata as a string part under the key "metadata"
    formData.append("metadata", JSON.stringify(metadata));

    const res = await axios.post('/adriva_api/dq/execute_with_upload', formData, {
        headers: { "Content-Type": "multipart/form-data" }
    });

    return res.data;

}

export function adriva_getLatestDQJobDetails(entityId: string, dmId: string, success:any, error:any){
    axios.post('/adriva_api/job/get_latest_dq_job_details',{entityId, dmId}).then((response:any)=> success(response.data)).catch(error);
}


const _XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
 
/**
 * POST the report request and return the workbook as a Blob.
 * Throws on failure (the server still saved a copy under data/dq_reports/).
 */
export async function adriva_downloadDQReport(
  entityId: string,
  dmId: string,
): Promise<Blob> {
  const response = await axios.post(
    `/adriva_api/dq/download-dq-report`,
    { entityId, dmId },
    {
      responseType: "blob",
      timeout: 10 * 60 * 1000, // large xlsx can take a while; 0 = no timeout
    },
  );
  return new Blob([response.data], { type: _XLSX_MIME });
}
 
/**
 * Convenience: POST, then trigger the browser "Save As" using a caller-supplied
 * filename. Falls back to the server-saved file (GET) if the POST blob fails.
 */
export async function adriva_downloadDQReportAndSave(
  entityId: string,
  dmId: string,
  fileName?: string,
): Promise<void> {
  try {
    const response = await axios.post(
      `/adriva_api/dq/download-dq-report`,
      { entityId, dmId },
      { responseType: "blob", timeout: 10 * 60 * 1000 },
    );
    const cd = response.headers?.["content-disposition"] || "";
    const m = /filename="?([^"]+)"?/.exec(cd);
    const serverName = response.headers?.["x-dq-report-file"];
    const name =
      fileName || (m && m[1]) || serverName || `DQ_Report_${entityId}.xlsx`;
    _saveBlob(new Blob([response.data], { type: _XLSX_MIME }), name);
  } catch (err: any) {
    // server kept a copy — try the GET fallback if we know its name
    const savedName = err?.response?.headers?.["x-dq-report-file"];
    if (savedName) {
      try {
        await adriva_downloadSavedDQReport(savedName, fileName);
        return;
      } catch {
        /* fall through */
      }
    }
    console.error("[DQ report] download failed", err);
    throw err;
  }
}
 
/** GET an already-saved report from the server (no rebuild). */
export async function adriva_downloadSavedDQReport(
  fileName: string,
  saveAs?: string,
): Promise<void> {
  const res = await axios.get(
    `/adriva_api/dq/download-dq-report-file/${encodeURIComponent(fileName)}`,
    { responseType: "blob", timeout: 10 * 60 * 1000 },
  );
  _saveBlob(new Blob([res.data], { type: _XLSX_MIME }), saveAs || fileName);
}
 
function _saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.parentNode?.removeChild(a);
  URL.revokeObjectURL(url);
}


export function adriva_dq_postAction(input: string, success:any, error:any){
    axios.post('/adriva_api/dq/rules/action',{input}).then((response:any)=> success(response.data)).catch(error);
}

//////////////////////////////

export function idmc_login(username: string, password: string, success:any, error:any){
    axios.post('/idmc-login',{username, password}).then((response:any)=> success(response.data)).catch(error);
}

export function idmc_getProjectList(IDMC_SESSION_ID: string, IDMC_BASE_APP_URL: string, success:any, error:any){
    axios.post('/idmc-getProjectList',{IDMC_SESSION_ID, IDMC_BASE_APP_URL}).then((response:any)=> success(response.data)).catch(error);
}

export function idmc_getFolderList(IDMC_SESSION_ID: string, IDMC_BASE_APP_URL: string, projId: string, success:any, error:any){
    axios.post('/idmc-getFolderList',{IDMC_SESSION_ID, IDMC_BASE_APP_URL, proj_id: projId}).then((response:any)=> success(response.data)).catch(error);
}

export function idmc_getSourceRank(IDMC_SESSION_ID: string, IDMC_BASE_APP_URL: string, guid: string, success:any, error:any){
    axios.post('/idmc-getSourceRank',{IDMC_SESSION_ID, IDMC_BASE_APP_URL, guid}).then((response:any)=> success(response.data)).catch(error);
}

export function idmc_migrate(IDMC_SESSION_ID: string, IDMC_BASE_APP_URL: string, folderId: string, config: string, success:any, error:any){
    axios.post('/idmc-migrate',{IDMC_SESSION_ID, IDMC_BASE_APP_URL, folderId, config}).then((response:any)=> success(response.data)).catch(error);
}

//// RELTIO
export function reltio_login(CLIENT_ID: string, CLIENT_SECRET: string, USER_ID: string, USER_PWD: string, success:any, error:any){
    axios.post('/adriva_api/reltio/login',{CLIENT_ID, CLIENT_SECRET, USER_ID, USER_PWD}).then((response:any)=> success(response.data)).catch(error);
}

export function get_reltio_migrate(config: string, exisConfig: string|null, success:any, error:any){
    axios.post('/adriva_api/reltio/get-migrate',{config, exisConfig}).then((response:any)=> success(response.data)).catch(error);
}

export function reltio_migrate(authConfig: string, config: string, exisConfig: string|null, success:any, error:any){
    axios.post('/adriva_api/reltio/migrate',{authConfig, config, exisConfig}).then((response:any)=> success(response.data)).catch(error);
}




// MDM-Modernization
export async function adriva_uploadInfaConfigFiles(appName: string, xmlFile: File, jsonFile: File): Promise<any> {
  const formData = new FormData();
  formData.append("appName", appName);
  formData.append("xmlFile", xmlFile);
  formData.append("jsonFile", jsonFile);

  const res = await axios.post('/adriva_api/mdm_modern/uploadInfaConfigFiles', formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });

  return res.data;
}

export function adriva_getRawMetadata(appName: string, success:any, error:any){
    axios.post('/adriva_api/mdm_modern/getRawMetadata',{appName}).then((response:any)=> success(response.data)).catch(error);
}

export function adriva_convertToRefinedDM(appName: string, topTableName: string, config: string, srcSystem: string, success:any, error:any){
    axios.post('/adriva_api/mdm_modern/convertToRefinedDM',{appName, topTableName, config, srcSystem}).then((response:any)=> success(response.data)).catch(error);
}

export function adriva_getEntityRelationships(appName: string, tableList: string, success:any, error:any){
    axios.post('/adriva_api/mdm_modern/getEntityRelationships',{appName, tableList}).then((response:any)=> success(response.data)).catch(error);
}

// Generate a new process ID (UUID v4)
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}


export async function adriva_runPipeline(
  appNm:      string,
  entityId:   string,
  processId?: string,          // original process_id on resume; omit/empty for fresh
  jobTypes?:  string[],
  resume?:    boolean,
): Promise<{ 
    success: boolean; 
    status?: string; 
    process_id?: string; 
    monitor_url?: string; 
    message?: string;
    // Conflict fields (returned when status === "conflict")
    active_process_id?: string;
    active_job_types?: string[];
    active_started_at?: string;
 }> {
  //const processId = generateUUID();
  const urlProcessId = (resume && processId) ? processId : "new";
  const response = await axios.post(`/adriva_api/pipeline/${urlProcessId}/run`, { 
    session_id: '111',
    app_nm: appNm,
    entity_id: entityId,
    gen_ai_model: "GEMINAI",
    jobs:  jobTypes?.length ? jobTypes : undefined,
    resume:     resume || false,
  });
  return { ...response.data, process_id: processId || response.data.process_id };
}

export const adriva_getPipelineStatus = async (processId: string): Promise<{
  success: boolean;
  process_id: string;
  jobs: Record<string, { job_id: string; status: string; progress: number }>;
}> => {
  return apiGet(`/adriva_api/pipeline/${processId}/status`);
};




export async function adriva_uploadFromConnection(
  payload: UploadFromConnectionPayload,
): Promise<UploadFromConnectionResponse> {
  

  const response = await axios.post(`/adriva_api/uploadFromConnection`, {
    ...payload
  });

  return { 
    ...response.data
  };
}


export function adriva_dg_postAction(
  input: string,
  success: (data: any) => void,
  error: (err: any) => void,
) {
  axios
    .post("/adriva_api/dg/artifacts/action", { input })
    .then((response: any) => success(response.data))
    .catch(error);
}

export function adriva_catalog_postAction(
  input: string,
  success: (data: any) => void,
  error: (err: any) => void
): void {
  axios
    .post("/adriva_api/catalog/action", { input })
    .then((response: any) => success(response.data))
    .catch(error);
}
 


export async function adriva_exportGovernanceExcel(
  appNm: string,
  entityId: string,
  entities: Array<{
    entity_key: string;
    entity_name: string;
    entity_metadata: any;
    fields: any[];
  }>,
  entityIds?: string[]   // ★ NEW: server-side export by entity IDs (no field data needed)
): Promise<void> {
  const response = await axios.post(
    "/adriva_api/dg/artifacts/export-excel",
    {
      app_nm: appNm,
      entity_id: entityId,
      entities,
      ...(entityIds?.length ? { entity_ids: entityIds } : {}),
    },
    { responseType: "blob" }
  );

  const url      = URL.createObjectURL(new Blob([response.data]));
  const link     = document.createElement("a");
  link.href      = url;
  link.download  = `${appNm}_governance_export.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ── DDL upload ────────────────────────────────────────────────────────────────
export interface DDLColumn {
  name: string;
  data_type: string;
  length?: number | null;
  precision?: number | null;
  scale?: number | null;
  nullable: boolean;
  is_pk: boolean;
  description?: string;
}

export interface DDLTable {
  table_name: string;
  description?: string;
  columns: DDLColumn[];
}

export interface DDLRelationship {
  from_table: string;
  from_column: string;
  to_table: string;
  to_column: string;
}

export interface UploadFromDDLPayload {
  tool: string;
  action?: string;
  app_nm: string;
  business_context?: string;
  domain?: string;
  entity?: string;
  entity_description?: string;
  system_name?: string;
  tables: DDLTable[];
  relationships?: DDLRelationship[];
  sheet_mode?: "sub_entities" | "separate_entities";
  dg_config?: any;
}

export async function adriva_uploadFromDDL(
  payload: UploadFromDDLPayload
): Promise<any> {
  return apiPost("/adriva_api/uploadFromDDL", payload);
}

// ── Catalog import ────────────────────────────────────────────────────────────
export interface CatalogColumn extends DDLColumn {
  business_term?: string;
  classification?: Record<string, any>;
  ownership?: string;
}

export interface CatalogTable {
  table_name: string;
  description?: string;
  columns: CatalogColumn[];
}

export interface UploadFromCatalogPayload {
  tool: string;
  action?: string;
  app_nm: string;
  business_context?: string;
  domain?: string;
  entity?: string;
  entity_description?: string;
  system_name?: string;
  target: "collibra" | "alation";
  source_label?: string;
  tables: CatalogTable[];
  relationships?: DDLRelationship[];
  sheet_mode?: "sub_entities" | "separate_entities";
  dg_config?: any;
}

export async function adriva_uploadFromCatalog(
  payload: UploadFromCatalogPayload
): Promise<any> {
  return apiPost("/adriva_api/uploadFromCatalog", payload);
}

// ── Catalog actions (used by CatalogSource UI) ────────────────────────────────
export async function catalog_action(payload: any): Promise<any> {
  return apiPost("/adriva_api/catalog/action", { input: JSON.stringify(payload) });
}

// ── Business reference files ──────────────────────────────────────────────────

export interface BusinessFileManifestEntry {
  name:        string;
  size_bytes:  number;
  uploaded_at: string;
}

export interface BusinessFilesListResponse {
  success: boolean;
  files:   BusinessFileManifestEntry[];
  app_nm:  string;
}

/** List all uploaded business files for an app. */
export async function adriva_listBusinessFiles(
  app_nm: string
): Promise<BusinessFilesListResponse> {
  return apiGet(`/adriva_api/businessFiles/${encodeURIComponent(app_nm)}`);
}

/**
 * Upload one or more business reference files for an app.
 * Uses multipart/form-data — does NOT use apiPost (which sends JSON).
 */
export async function adriva_uploadBusinessFiles(
  app_nm: string,
  files: File[]
): Promise<{
  success: boolean;
  uploaded: { name: string; size_bytes: number }[];
  errors: { name: string; error: string }[];
}> {
  const form = new FormData();
  files.forEach((f) => form.append("files", f, f.name));

  const res = await api.post(
    `/adriva_api/uploadBusinessFiles/${encodeURIComponent(app_nm)}`,
    form
  );

  return res.data;
}

/** Delete one business reference file. */
export async function adriva_deleteBusinessFile(
  app_nm: string,
  file_name: string
): Promise<{ success: boolean }> {
  const base = (window as any).__ADRIVA_API_BASE__ || "";

  return apiDelete<{ success: boolean }>(
    `${base}/adriva_api/businessFiles/${encodeURIComponent(app_nm)}/${encodeURIComponent(file_name)}`
  );
}

// ── DQ Token Map ──────────────────────────────────────────────────────────────
export interface TokenMapResponse {
  success: boolean;
  data?:   any;           // TokenBlock — keep `any` here to avoid a circular
                          // import with TokenMapEditor; cast on the caller.
  detail?: string;
}

export interface TokenMapSavePayload {
  mapping?:     Record<string, string>;
  regex_pairs?: Array<{ pattern: string; replacement: string }>;
  config?:      Record<string, any>;
  dsl_fn?:      string | null;
  field_arg?:   any;
  token_type?:  string;
  sme_note?:    string | null;
}

function tokenMapPath(
  entityId: string, sourcePrefix: string, column: string, ruleId: string,
): string {
  // NOTE: confirm this base path with backend — other DQ endpoints sit under
  // /adriva_api/dq/... If this router is mounted there too, update accordingly.
  return `/adriva_api/dq/token-map/${encodeURIComponent(entityId)}`
       + `/${encodeURIComponent(sourcePrefix)}`
       + `/${encodeURIComponent(column)}`
       + `/${encodeURIComponent(ruleId)}`;
}

export async function adriva_dq_getEntityTokenMap(entityId: string) {
  return apiGet(`/adriva_api/dq/token-map/${encodeURIComponent(entityId)}`);
}

export async function adriva_dq_getTokenMap(
  entityId: string, sourcePrefix: string, column: string, ruleId: string,
): Promise<TokenMapResponse> {
  return apiGet<TokenMapResponse>(
    tokenMapPath(entityId, sourcePrefix, column, ruleId),
  );
}

export async function adriva_dq_saveTokenMap(
  entityId: string, sourcePrefix: string, column: string, ruleId: string,
  body: TokenMapSavePayload,
): Promise<TokenMapResponse> {
  return apiPut<TokenMapResponse>(
    tokenMapPath(entityId, sourcePrefix, column, ruleId),
    body,
  );
}


// ─── Canonical Token Map API helpers ─────────────────────────────────────────
// Add these to your existing API.ts

// Types
export interface CanonicalValueObj {
  value:       string;
  label:       string;
  description: string;
  is_active:   boolean;
  sort_order:  number;
  sme_note:    string;
  source:      'derived' | 'sme_added';
}

export interface CrosswalkEntry {
  entity_id:     string;
  source_prefix: string;
  column:        string;
  rule_id:       string;
  raw_values:    string[];
  sme_remap:     string | null;
}

export interface CanonicalDomain {
  token_type:       string;
  canonical_values: CanonicalValueObj[];
  crosswalk:        Record<string, CrosswalkEntry[]>;
}

export interface CanonicalTokenMap {
  canonical_entity_id: string;
  source_entity_ids:   string[];
  generated_at:        string;
  version:             number;
  domains:             Record<string, CanonicalDomain>;
}

export interface DomainSummary {
  token_type:           string;
  canonical_count:      number;
  active_count:         number;
  sme_added_count:      number;
  total_raw_variants:   number;
  source_count:         number;
  remapped_count:       number;
}

// Build / rebuild canonical token map
export async function adriva_canonical_buildTokenMap(
  canonicalEntityId: string,
  appNm: string,
  preserveSmeEdits: boolean = true,
) {
  return apiPost('/adriva_api/canonical/token-map/build', {
    canonical_entity_id: canonicalEntityId,
    app_nm:              appNm,
    preserve_sme_edits:  preserveSmeEdits,
  });
}

// Load full canonical map
export async function adriva_canonical_getTokenMap(canonicalEntityId: string) {
  return apiGet(`/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}`);
}

// Load summary (sidebar stats)
export async function adriva_canonical_getTokenMapSummary(canonicalEntityId: string) {
  return apiGet(`/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}/summary`);
}

// Load single domain
export async function adriva_canonical_getDomain(canonicalEntityId: string, tokenType: string) {
  return apiGet(
    `/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}/domain/${encodeURIComponent(tokenType)}`
  );
}

// Patch a canonical value (label/description/is_active/sort_order/sme_note)
export async function adriva_canonical_patchCanonicalValue(
  canonicalEntityId: string,
  tokenType:         string,
  value:             string,
  patch:             Partial<CanonicalValueObj>,
) {
  return apiGet(
    `/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}/domain/${encodeURIComponent(tokenType)}/value/${encodeURIComponent(value)}`,
    patch,
  );
}

// Add new canonical value (SME)
export async function adriva_canonical_addCanonicalValue(
  canonicalEntityId: string,
  tokenType:         string,
  newValue:          Partial<CanonicalValueObj> & { value: string },
) {
  return apiPost(
    `/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}/domain/${encodeURIComponent(tokenType)}/value`,
    newValue,
  );
}

// Soft-delete (deactivate) a canonical value
export async function adriva_canonical_deactivateCanonicalValue(
  canonicalEntityId: string,
  tokenType:         string,
  value:             string,
) {
  return apiDelete(
    `/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}/domain/${encodeURIComponent(tokenType)}/value/${encodeURIComponent(value)}`
  );
}

// Remap a crosswalk entry to a different canonical value
export async function adriva_canonical_remapCrosswalk(
  canonicalEntityId: string,
  domainKey: string,
  oldCanonical: string,
  newCanonical: string,
  entry: {
    entity_id: string;
    source_prefix: string;
    column: string;
    rule_id: string;
    used_by_canonical_field?: string;  // NEW — needed for shared domains
  },
) {
  return apiPost(
    `/adriva_api/canonical/token-map/${encodeURIComponent(canonicalEntityId)}/domain/${encodeURIComponent(domainKey)}/crosswalk/remap`,
    {
      old_canonical: oldCanonical,
      new_canonical: newCanonical,
      ...entry,
    },
  );
}

export const adriva_getJobsSummary = (
  appNm: string,
  onSuccess: (res: any) => void,
  onError: () => void
) => {
  return apiGet(`/api/jobs/summary/${appNm}`);
};


// ── Batch pipeline execution ──────────────────────────────────────────────────
// Add these to API.ts alongside the existing adriva_runPipeline / adriva_getPipelineStatus
// ── Batch pipeline execution ──────────────────────────────────────────────────
// Add these to API.ts alongside adriva_runPipeline / adriva_getPipelineStatus

export interface BatchEntityItem {
  entity_id:    string;
  entity_label: string;
}

export interface BatchRunRequest {
  app_nm:        string;
  entities:      BatchEntityItem[];
  jobs?:         string[];    // undefined / [] = run all jobs
  session_id?:   string;
  gen_ai_model?: string;
}

export interface BatchRunResponse {
  success:       boolean;
  batch_id?:     string;
  entity_count?: number;
  status_url?:   string;
  sse_url?:      string;
  message?:      string;
}

export interface BatchEntityProgress {
  entity_id:    string;
  entity_label: string;
  status:       "queued" | "running" | "done" | "failed";
  process_id?:  string;   // use to drill into JobMonitor
  started_at?:  string;
  finished_at?: string;
  error?:       string | null;
}

export interface BatchStatusResponse {
  success:      boolean;
  batch_id:     string;
  status:       "queued" | "running" | "done" | "failed";
  app_nm?:      string;
  total?:       number;
  created_at?:  string;
  finished_at?: string;
  entities:     BatchEntityProgress[];
}


export async function adriva_getActivePipeline(entityId: string): Promise<{
  active: boolean;
  process_id?: string;
  job_types?: string[];
  started_at?: string;
}> {
  const res = await axios.get(`/adriva_api/pipeline/active/${entityId}`);
  return res.data;
}

export async function adriva_cancelPipeline(entityId: string): Promise<{
  success: boolean;
  message: string;
}> {
  const res = await axios.post(`/adriva_api/pipeline/cancel/${entityId}`);
  return res.data;
}

/**
 * Submit a multi-entity batch job to the server queue.
 * Returns immediately with a batch_id — server runs entities sequentially.
 * Browser can close after calling this; execution continues on the server.
 *
 * Usage (matches how JobExecutionPage calls it):
 *   const res = await adriva_runBatch({
 *     app_nm:   appNm,
 *     entities: selectedEntities.map(e => ({ entity_id: e.entityId, entity_label: e.label })),
 *     jobs:     runAll ? undefined : [...activeJobTypes],
 *   });
 */
export async function adriva_runBatch(
  body: BatchRunRequest,
): Promise<BatchRunResponse> {
  const response = await axios.post("/adriva_api/pipeline/batch/run", {
    app_nm:       body.app_nm,
    session_id:   body.session_id   ?? "",
    gen_ai_model: body.gen_ai_model ?? "GEMINAI",
    entities:     body.entities,
    jobs:         body.jobs && body.jobs.length > 0 ? body.jobs : undefined,
  });
  return response.data;
}

/**
 * Poll batch progress.
 * Call every 5s; stop polling when status === "done" || "failed".
 */
export async function adriva_getBatchStatus(
  batchId: string,
): Promise<BatchStatusResponse> {
  return apiGet<BatchStatusResponse>(
    `/adriva_api/pipeline/batch/${batchId}/status`
  );
}

/**
 * List recent batch runs for an app (last 20, newest first).
 */
export async function adriva_listBatches(
  appNm: string,
): Promise<{ success: boolean; data: any[] }> {
  return apiGet(`/adriva_api/pipeline/batch/list/${encodeURIComponent(appNm)}`);
}


// ═════════════════════════════════════════════════════════════════════════════


// ─── DG: Entity artifacts (single + batch) ────────────────────────────────────

export interface DGEntityArtifacts {
  entity_id:                string;
  entity_dm:                any[];        // sliced from <app>.json refinedDM
  business_metadata:        any[];
  metadata_classifications: any[];
  cde_assessments:          any[];
  lifecycle_map:            Record<string, string>;
  catalog_credentials:      Record<string, any>;
  catalog_mapping:          Record<string, any>;
  dg_config:                any;
  dg_config_source?:        string;
  error?:                   string;
}

export interface DGEntityArtifactsResponse {
  success: boolean;
  data:    DGEntityArtifacts;
}

export interface DGEntityArtifactsBatchResponse {
  success: boolean;
  count:   number;
  data:    Record<string, DGEntityArtifacts>;  // keyed by entity_id
  skipped: string[];
}

/** Fetch all governance artifacts for ONE entity (small payload, server-joined). */
export async function adriva_dg_getEntityArtifacts(
  appNm: string,
  entityId: string,
): Promise<DGEntityArtifactsResponse> {
  return apiGet<DGEntityArtifactsResponse>(
    `/adriva_api/dg/entity-artifacts/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
  );
}

/** Fetch artifacts for MANY entities in one call. Used after the SME picks
 *  a subset of entities from the SourceDashboard. */
export async function adriva_dg_getEntityArtifactsBatch(
  appNm: string,
  entityIds: string[],
): Promise<DGEntityArtifactsBatchResponse> {
  return apiPost<DGEntityArtifactsBatchResponse>(
    `/adriva_api/dg/entity-artifacts/${encodeURIComponent(appNm)}/batch`,
    { entity_ids: entityIds },
  );
}


// ─── DG: Source-level summary ─────────────────────────────────────────────────

export interface DGEntitySummary {
  entity_id:         string;
  entity_label:      string;
  domain:            string | null;
  src_system_id:     string | null;
  src_system_name:   string | null;
  has_process_data:  boolean;
  field_count:       number;
  approved_count:    number;
  approval_pct:      number;
  sensitivity:       Record<string, number>;
  cde_tier:          Record<string, number>;
  criticality:       Record<string, number>;
  regulatory:        Record<string, number>;
  risk_buckets:      Record<string, number>;
  personal_data:     number;
  anon_required:     number;
  cde_count:         number;
  critical_cde:      number;
  dm_field_count:  number;
  bm_enriched:     number;
  mc_enriched:     number;
  cde_enriched:    number;
}

export interface DGSourceAggregate {
  entity_count:   number;
  total_fields:   number;
  approved_count: number;
  approval_pct:   number;
  personal_data:  number;
  anon_required:  number;
  cde_count:      number;
  critical_cde:   number;
  sensitivity:    Record<string, number>;
  cde_tier:       Record<string, number>;
  criticality:    Record<string, number>;
  regulatory:     Record<string, number>;
  risk_buckets:   Record<string, number>;
}

export interface DGSourceSummaryResponse {
  success: boolean;
  source: {
    app_nm:          string;
    src_system_id:   string | null;
    src_system_name: string | null;
  };
  entities:  DGEntitySummary[];
  aggregate: DGSourceAggregate;
}

/**
 * Fetch source-level summary KPIs (lightweight — counters only, no fields).
 *
 * @param appNm        App name.
 * @param srcSystemId  If supplied, only entities under that source.
 * @param entityIds    If supplied, further restrict (e.g. SME checkbox subset).
 */
export async function adriva_dg_getSourceSummary(
  appNm: string,
  srcSystemId?: string,
  entityIds?: string[],
): Promise<DGSourceSummaryResponse> {
  const params: Record<string, string> = {};
  if (srcSystemId) params.src_system_id = srcSystemId;
  if (entityIds && entityIds.length > 0) params.entity_ids = entityIds.join(",");
  return apiGet<DGSourceSummaryResponse>(
    `/adriva_api/dg/source-summary/${encodeURIComponent(appNm)}`,
    params,
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// API.ts  — APPEND these functions to the bottom of your existing API.ts
//
// All new functions use the same apiGet helper already defined in API.ts.
// No new imports needed.
//
// Types are plain interfaces — no "Lazy" prefix.
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// API.ts  — APPEND these functions to the bottom of your existing API.ts
//
// All new functions use the same apiGet helper already defined in API.ts.
// No new imports needed.
//
// Types are plain interfaces — no "Lazy" prefix.
// ─────────────────────────────────────────────────────────────────────────────

// ── URL bases ─────────────────────────────────────────────────────────────────
const _BASE    = '/adriva_api/lazy';     // app_router.py lazy endpoints
const _BASE_DQ = '/adriva_api/dq/lazy';  // dq_router.py  lazy endpoints

// ── Lazy-loading config (single source of truth) ──────────────────────────────
// Tune these in ONE place. Backend has its own safety caps (limit ≤ 200,
// hydrate ≤ 500); these are the values the client actually requests.
export const LAZY_CONFIG = {
  /** fields fetched per sub-entity page (initial load + each "load more") */
  PAGE_SIZE: 200,
  /** how many sub-entities are hydrated WITH fields on entity click;
   *  the rest render header-only (name + field count) and load on demand */
  HYDRATE_SUBENTITIES: 20,
} as const;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SourceSystem {
  srcSystemId:   string;
  srcSystemName: string;
}

export interface AppEntity {
  entityId:  string;
  entity:    string;
  domain:    string;
  processId: string;
}

export interface SubEntity {
  name:        string;
  label:       string;
  field_count: number;
  is_root:     boolean;
}

export interface DMIndex {
  success:      boolean;
  entity_id:    string;
  entity_label: string;
  sub_entities: SubEntity[];
}

export interface DMEntry {
  success:   boolean;
  entity_id: string;
  entry:     any;   // a single full refinedDM entry (intact field subtree)
}

// Combined DM + profile (+ rules), paginated per sub-entity.
export interface DMSubStats {
  total:    number;
  nullCols: number;
  single:   number;
  two:      number;
  multi:    number;
  avgComp:  number | null;
}

export interface DMProfileSubEntity {
  name:        string;
  label:       string;
  field_count: number;
  is_root:     boolean;
  total:       number;
  loaded:      number;
  has_more:    boolean;
  hydrated:    boolean;   // false = header-only, fields load on demand
  stats?:      DMSubStats; // server-side column rollups over ALL columns
  fields:      any[];     // DM leaf fields, each with a `.profile` already attached
}

export interface DMProfile {
  success:      boolean;
  entity_id:    string;
  entity_label: string;
  hydrate:      number;
  sub_entities: DMProfileSubEntity[];
}

export interface DMProfilePage {
  success:   boolean;
  entity_id: string;
  name:      string;   // sub-entity name
  offset:    number;
  limit:     number;
  total:     number;
  has_more:  boolean;
  fields:    any[];    // DM leaf fields with `.profile` attached
}

export interface DMPage {
  success:    boolean;
  entity_id:  string;
  sub_entity: string;
  offset:     number;
  limit:      number;
  total:      number;
  has_more:   boolean;
  fields:     any[];
}

export interface ProfilePage {
  success:    boolean;
  entity_id:  string;
  sub_entity: string;
  offset:     number;
  limit:      number;
  total:      number;
  has_more:   boolean;
  columns:    any[];
}

export interface DQRulesPage {
  success:    boolean;
  entity_id:  string;
  sub_entity: string;
  source:     'dq_rules_file' | 'process_data_fallback';
  offset:     number;
  limit:      number;
  total:      number;
  has_more:   boolean;
  columns:    Array<{
    source_prefix:    string;
    column_name:      string;
    orig_column_name: string;
    data_type:        string;
    insight: {
      validation_rules:   any[];
      validation_results: any[];
    };
  }>;
}

export interface EntityStats {
  success:         boolean;
  entity_id:       string;
  high_stats:      any;
  table_summaries: any[];
  dq_checkpoint:   any;
}

export interface DQLifecycleSummary {
  success:      boolean;
  entity_id:    string;
  source:       string;
  total:        number;
  by_lifecycle: Record<string, number>;
  by_category:  Record<string, number>;
  by_dimension: Record<string, number>;
  by_source?:   Record<string, number>;                 
  rule_index?:  Array<{                                  
       id: string; lifecycle: string; cat: string; source: 'llm' | 'policy';
  }>;
}

export interface SampleData {
  success:    boolean;
  entity_id:  string;
  sub_entity: string;
  samples:    Record<string, any>;
}


// ── DQ Assessment / Migration Readiness ───────────────────────────────────────
export interface DQAssessment {
  success:    boolean;
  run_mode:   string;
  status:     string;
  entity_id?: string;
  app_nm?:    string;
  overall: {
    readiness:       number;
    objects:         number;
    total_records:   number;
    total_rules:     number;
    blocker_count:   number;
    rules_failing?:  number;
    pass_rate?:      number | null;
    fields_failing?: number;
  };
  rollup: {
    by_dimension: Record<string, number>;
    by_severity:  Record<string, number>;
    by_category:  Record<string, number>;
    execution?:   any;
    top_risks:    any[];
  };
  apply: {
    counts:        { auto: number; assisted: number; manual: number };
    rationale:     { auto: string; assisted: string; manual: string };
    auto_summary:  { applied: number; no_change_needed: number; records_cleansed: number };
    lanes:         { auto: any[]; assisted: any[]; manual: any[] };
    sme_queue:     any[];
  };
  methodology?: any;
  tables: any[];
}

// ── Source files (for DQ Rule Execution upload, lazy Jobs page) ───────────────
export interface SourceFile {
  origName:    string;
  label:       string;
  columnCount: number;
  id:          string;
}
 
/** Source CSV/table groupings an entity needs for DQ Rule Execution.
 *  Used by the Jobs page when the DM tree hasn't loaded the entity's fields. */
export async function getEntitySourceFiles(
  appNm:    string,
  entityId: string,
): Promise<{ success: boolean; tables: SourceFile[] }> {
  return apiGet(
    `${_BASE}/source-files/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
  );
}

// ── API functions ─────────────────────────────────────────────────────────────

/** Source systems list for an app (~0.5 KB). */
export async function getSources(
  appNm: string,
): Promise<{ success: boolean; data: SourceSystem[] }> {
  return apiGet(`${_BASE}/sources/${encodeURIComponent(appNm)}`);
}

/** Entity list for one source system (~1–2 KB). */
export async function getEntities(
  appNm:       string,
  srcSystemId: string,
): Promise<{ success: boolean; data: AppEntity[] }> {
  return apiGet(
    `${_BASE}/entities/${encodeURIComponent(appNm)}/${encodeURIComponent(srcSystemId)}`,
  );
}

/** Sub-entity index — names + field counts, no field data (~2 KB). */
export async function getDMIndex(
  appNm:    string,
  entityId: string,
): Promise<DMIndex> {
  return apiGet(
    `${_BASE}/dm/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
  );
}

/**
 * One full refinedDM entry for a single entity (entire field subtree, intact).
 * Lazy replacement for loading the whole app.json refinedDM — load only the
 * entity the user is viewing, on demand. ~tens of KB per entity.
 */
export async function getDMEntry(
  appNm:    string,
  entityId: string,
): Promise<DMEntry> {
  return apiGet(
    `${_BASE}/dm-entry/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
  );
}



/**
 * On entity click: first `limit` fields (default 50) of EVERY sub-entity, each
 * field already carrying its profile stats + DQ rules (server-side join on
 * ORIG_COLUMN_NAME). Renders immediately, no client-side merge. Per sub-entity
 * total/has_more drive a "Load more" button.
 */
export async function getDMProfile(
  appNm:    string,
  entityId: string,
  limit     = LAZY_CONFIG.PAGE_SIZE,
  hydrate   = LAZY_CONFIG.HYDRATE_SUBENTITIES,
): Promise<DMProfile> {
  return apiGet(
    `${_BASE}/dm-profile/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
    { limit, hydrate },
  );
}

/** Next page of fields (with profile + rules) for ONE sub-entity — the "Load more". */
export async function getDMProfilePage(
  appNm:     string,
  entityId:  string,
  subEntity: string,
  offset     = 0,
  limit      = LAZY_CONFIG.PAGE_SIZE,
): Promise<DMProfilePage> {
  return apiGet(
    `${_BASE}/dm-profile/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}/${encodeURIComponent(subEntity)}`,
    { offset, limit },
  );
}

/** Paginated DM fields for one sub-entity (~15 KB / 50 fields). */
export async function getDMPage(
  appNm:     string,
  entityId:  string,
  subEntity: string,
  offset     = 0,
  limit      = 50,
): Promise<DMPage> {
  return apiGet(
    `${_BASE}/dm/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
    { sub_entity: subEntity, offset, limit },
  );
}

/** Entity-level KPIs — high_stats + table_summaries + dq_checkpoint (~5 KB). */
export async function getEntityStats(entityId: string): Promise<EntityStats> {
  return apiGet(`${_BASE}/entity-stats/${encodeURIComponent(entityId)}`);
}

/**
 * Paginated per-column profile stats for one sub-entity (~100 KB / 50 cols).
 * No sample rows. No DQ rules.
 * includeInsight=true adds modeling_recommendations, quality_issues etc.
 */
export async function getProfilePage(
  entityId:      string,
  subEntity:     string,
  offset         = 0,
  limit          = 50,
  includeInsight = false,
): Promise<ProfilePage> {
  return apiGet(
    `${_BASE}/profile/${encodeURIComponent(entityId)}/${encodeURIComponent(subEntity)}`,
    { offset, limit, include_insight: includeInsight },
  );
}

/** On-demand sample rows — call only when user explicitly requests them. */
export async function getSamples(
  entityId:  string,
  subEntity: string,
): Promise<SampleData> {
  return apiGet(
    `${_BASE}/samples/${encodeURIComponent(entityId)}/${encodeURIComponent(subEntity)}`,
  );
}

/** Aggregate DQ rule counts across all sub-entities (~1 KB). */
export async function getDQSummary(entityId: string): Promise<DQLifecycleSummary> {
  return apiGet(`${_BASE_DQ}/dq-summary/${encodeURIComponent(entityId)}`);
}

/**
 * Paginated DQ rules for one sub-entity (~80 KB / 50 cols).
 * Prefers <entity_id>_DQ_RULES.json; falls back to PROCESS_DATA for older entities.
 */
export async function getDQRulesPage(
  entityId:  string,
  subEntity: string,
  offset     = 0,
  limit      = 50,
): Promise<DQRulesPage> {
  return apiGet(
    `${_BASE_DQ}/dq-rules/${encodeURIComponent(entityId)}/${encodeURIComponent(subEntity)}`,
    { offset, limit },
  );
}


/** Deterministic migration-readiness assessment for one entity. */
export async function getAssessment(
  appNm:    string,
  entityId: string,
  runMode   = 'sample',     // 'sample' | 'actual'
  status    = 'all',        // 'all' | 'approved'
): Promise<DQAssessment> {
  return apiGet(
    `${_BASE_DQ}/assessment/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
    { run_mode: runMode, status },
  );
}
 

export interface StageFieldStats {
  label: string; present: boolean;
  requested: number; completed: number; still_missing: number;
  pct: number; sweep_triggered: boolean; recovered: number;
  possibly_stuck: boolean; last_activity_ago_sec: number; stuck_threshold_sec: number;
}

export interface FieldStatsResponse {
  success: boolean; app_nm: string; entity_id: string;
  stages: Record<string, StageFieldStats | null>;   // keyed by job_type
}

/** Per-stage field completion for one entity — drives FieldCompletionCard.
 *  Works for any job type registered on the backend. */
export async function adriva_getFieldStats(
  appNm: string, entityId: string,
): Promise<FieldStatsResponse> {
  return apiGet<FieldStatsResponse>(
    `/adriva_api/job/field-stats/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
  );
}

